import React from 'react';

import PropTypes from 'prop-types';

import {
    Collapse,
    FadeInOutContainer,
    FadeInOut,
} from 'wanderu-animations';
import {
    CaretIcon,
    Slider,
    TabContainer,
    TabList,
    Tab,
    TabPanel,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import FilterEvents from '../events';
import Events from './events/TimeFilterEvents';
import {
    ARRIVAL_TYPE,
    DEPARTURE_TYPE,
} from '../../../constants';

import styles from './TimeFilter.scss';

const DIRECTION_TYPES = [DEPARTURE_TYPE, ARRIVAL_TYPE];

class TimeFilter extends React.PureComponent {
    static propTypes = {
        date: PropTypes.string.isRequired,
        i18nMoment: PropTypes.func,
        isArrivalTimingFiltered: PropTypes.bool.isRequired,
        isDepartureTimingFiltered: PropTypes.bool.isRequired,
        onArrivalTimingChange: PropTypes.func.isRequired,
        onArrivalTimingReset: PropTypes.func.isRequired,
        onDepartureTimingChange: PropTypes.func.isRequired,
        onDepartureTimingReset: PropTypes.func.isRequired,
        searchParams: PropTypes.shape({
            destinationPlaceId: PropTypes.string,
            destinationText: PropTypes.string,
            originPlaceId: PropTypes.string,
            originText: PropTypes.string,
        }).isRequired,
        t: PropTypes.func,
        timing: PropTypes.shape({
            arrival: PropTypes.arrayOf(PropTypes.number).isRequired,
            departure: PropTypes.arrayOf(PropTypes.number).isRequired,
        }).isRequired,
        totalTrips: PropTypes.number.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            activeDirection: DEPARTURE_TYPE,
            arrival: this.props.timing.arrival,
            departure: this.props.timing.departure,
            isOpen: true,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            arrival,
            departure,
        } = this.props.timing;
        const {
            arrival: prevArrival,
            departure: prevDeparture,
        } = prevProps.timing;

        const sameDepart = departure[0] === prevDeparture[0] && departure[1] === prevDeparture[1];
        const sameDepartState = prevState.departure[0] === this.state.departure[0]
            && prevState.departure[1] === this.state.departure[1];
        const sameDepartPropsAndState = departure[0] === this.state.departure[0]
            && departure[1] === this.state.departure[1];
        if (!sameDepart) {
            this.setState(() => ({ departure }));
        }
        if (sameDepart && sameDepartState && !sameDepartPropsAndState) {
            this.setState(() => ({ departure }));
        }

        const sameArrive = arrival[0] === prevArrival[0] && arrival[1] === prevArrival[1];
        const sameArriveState = prevState.arrival[0] === this.state.arrival[0]
            && prevState.arrival[1] === this.state.arrival[1];
        const sameArrivePropsAndState = arrival[0] === this.state.arrival[0]
            && arrival[1] === this.state.arrival[1];
        if (!sameArrive) {
            this.setState(() => ({ arrival }));
        }
        if (sameArrive && sameArriveState && !sameArrivePropsAndState) {
            this.setState(() => ({ arrival }));
        }
    }

    toggleRow = () => {
        const {
            searchParams,
        } = this.props;

        const eventFunction = this.state.isOpen
            ? FilterEvents.filterCollapsed
            : FilterEvents.filterExpanded;

        eventFunction({
            filterType: 'time',
            searchParams,
        });

        this.setState((currentState) => ({
            isOpen: !currentState.isOpen,
        }));
    };

    onTabChange = (direction) => {
        this.setState(() => ({ activeDirection: direction }));
    };

    onChange = (direction, value) => {
        this.setState(() => ({ [direction]: value }));
    };

    onAfterChange = (direction, value) => {
        const {
            searchParams,
            totalTrips,
        } = this.props;

        if (direction === DEPARTURE_TYPE) {
            this.props.onDepartureTimingChange(value);
            Events.departureTimeFiltered({
                searchParams,
                totalTrips,
                value,
            });
        } else {
            this.props.onArrivalTimingChange(value);
            Events.arrivalTimeFiltered({
                searchParams,
                totalTrips,
                value,
            });
        }
    };

    onReset = (direction) => {
        const {
            searchParams,
        } = this.props;

        FilterEvents.singleFilterReset({
            filterType: `${direction}_time`,
            searchParams,
        });

        return (direction === DEPARTURE_TYPE)
            ? this.props.onDepartureTimingReset()
            : this.props.onArrivalTimingReset();
    };

    renderTimeMarks(direction) {
        const {
            date,
            i18nMoment,
        } = this.props;
        const timeInterval = this.state[direction];

        const startDate = i18nMoment(date, 'YYYY-MM-DD');
        const startDay = startDate.format('ddd');
        const endDay = startDate.clone().add(1, 'days').format('ddd');

        return timeInterval.map((time, index) => (
            <span data-testid="time-mark" key={`${direction}-mark-${index}`} >
                {`${time < 24
                    ? startDay
                    : endDay} ${i18nMoment(time, 'H').format('LT')}`}
            </span>
        ));
    }

    shouldShowReset(direction) {
        const {
            isArrivalTimingFiltered,
            isDepartureTimingFiltered,
        } = this.props;

        return (direction === DEPARTURE_TYPE)
            ? isDepartureTimingFiltered
            : isArrivalTimingFiltered;
    }

    render() {
        const {
            t,
        } = this.props;
        const {
            activeDirection,
            isOpen,
        } = this.state;

        return (
            <div className={styles.timeTab}>
                <span
                    className={styles.toggleRow}
                    onClick={this.toggleRow}
                    role="button"
                >
                    <span className={styles.timeTitle}>{t('time')}</span>

                    <CaretIcon showUpArrow={isOpen} />
                </span>
                <Collapse
                    className={styles.collapse}
                    duration={500}
                    isOpen={isOpen}
                >
                    <TabContainer
                        activeId={activeDirection}
                        duration={500}
                        onChange={this.onTabChange}
                    >
                        <TabList className={styles.tabs}>
                            {DIRECTION_TYPES.map((type) => (
                                <Tab
                                    className={styles.tab}
                                    id={type}
                                    key={`${type}-tab`}
                                >
                                    {t(type)}
                                </Tab>
                            ))}
                        </TabList>
                        <FadeInOutContainer>
                            {DIRECTION_TYPES.map((type) => (
                                <FadeInOut
                                    className={styles.fadeInOut}
                                    key={`${type}-fade`}
                                    show={type === activeDirection}
                                >
                                    <TabPanel
                                        className={styles.tabPanel}
                                        id={type}
                                    >
                                        <div className={styles.timeMark}>
                                            {this.renderTimeMarks(type)}
                                        </div>
                                        <Slider
                                            aria-labelledby={type}
                                            classes={{ slider: styles.slider }}
                                            id={`${type}-slider`}
                                            maxValue={24}
                                            onChange={(value) => this.onChange(type, value)}
                                            onChangeEnd={(value) => this.onAfterChange(type, value)}
                                            thumbLabels={['start', 'end']}
                                            value={this.state[type]}
                                        />

                                        <div className={styles.resetButtonContainer}>
                                            <FadeInOutContainer
                                                animateHeight={false}
                                                duration={250}
                                            >
                                                <FadeInOut show={this.shouldShowReset(type)}>
                                                    <span
                                                        className={styles.resetButton}
                                                        onClick={() => this.onReset(type)}
                                                        role="button"
                                                    >
                                                        {t('reset')}
                                                    </span>
                                                </FadeInOut>
                                            </FadeInOutContainer>
                                        </div>
                                    </TabPanel>
                                </FadeInOut>
                            ))}
                        </FadeInOutContainer>
                    </TabContainer>
                </Collapse>
            </div>
        );
    }
}

export { TimeFilter };
export default localize(TimeFilter, 'baseApp');
